/** The Leaflet map: route, one layer group per narration, a car, and a two-click picker. */
import * as L from 'leaflet';
import { narrationVerdict, type SimNarration, type SimResult, type TrackPoint } from '@roadtrip/core';
import { isFeature } from './features';
import type { LatLon } from './route';

export class RouteMap {
  readonly map: L.Map;
  private route: L.Polyline | null = null;
  private sections = L.layerGroup();
  private picks = L.layerGroup();
  private car: L.Marker | null = null;
  private sectionLayers: L.LayerGroup[] = [];
  private pickHandler: ((p: LatLon) => void) | null = null;

  constructor(el: HTMLElement, private onSelect: (i: number) => void) {
    this.map = L.map(el, { zoomControl: true, attributionControl: true }).setView([45.6, -122.1], 10);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.map);
    this.sections.addTo(this.map);
    this.picks.addTo(this.map);
    // Rotation, the address bar and the route form all change the container; Leaflet does not notice on its own.
    new ResizeObserver(() => this.map.invalidateSize()).observe(el);
    this.map.on('click', (e: L.LeafletMouseEvent) => {
      if (!this.pickHandler) return;
      this.pickHandler({ lat: e.latlng.lat, lon: e.latlng.lng });
    });
  }

  setTrack(track: TrackPoint[]): void {
    this.clearResult();
    this.picks.clearLayers();
    this.route?.remove();
    this.route = L.polyline(
      track.map((p) => [p.lat, p.lon] as [number, number]),
      { className: 'lf-route', weight: 5, opacity: 1, color: '#000' },
    ).addTo(this.map);
    // The container may have been laid out after the map was created; measure again before fitting.
    this.map.invalidateSize();
    this.map.fitBounds(this.route.getBounds(), { padding: [30, 30] });
  }

  clearResult(): void {
    this.sections.clearLayers();
    this.sectionLayers = [];
    this.car?.remove();
    this.car = null;
  }

  showResult(r: SimResult): void {
    this.clearResult();
    r.narrations.forEach((n, i) => {
      const g = L.layerGroup();
      const seg = this.pathBetween(r, n.startPos?.t ?? n.startedAt, n.endedAt ?? r.durationSec);
      if (seg.length > 1) L.polyline(seg, { className: `lf-seg s${i % 6}`, weight: 7, color: '#000' }).addTo(g);
      if (isFeature(n)) {
        // A river is its line, a forest or wilderness its outline, as the corridor call sent it.
        const ft = n.feature.feature;
        const shape = ft.geom.map((part) => part.map(([la, lo]) => [la, lo] as [number, number]));
        const tip = `${i + 1}. ${ft.title} (${ft.kind})`;
        (ft.kind === 'river' ? L.polyline(shape, { className: 'lf-feature river', weight: 4 }) : L.polygon(shape, { className: `lf-feature ${ft.kind}`, weight: 1 }))
          .bindTooltip(tip, { className: 'lm', sticky: true })
          .on('click', () => this.onSelect(i))
          .addTo(g);
        if (n.startPos) L.circleMarker([n.startPos.lat, n.startPos.lon], { className: 'lf-start', radius: 4, weight: 2 }).addTo(g);
        g.addTo(this.sections);
        this.sectionLayers.push(g);
        return;
      }
      L.circleMarker([n.triggerPos.lat, n.triggerPos.lon], { className: 'lf-trig', radius: 5, weight: 2, fill: false }).addTo(g);
      if (n.startPos) L.circleMarker([n.startPos.lat, n.startPos.lon], { className: 'lf-start', radius: 4, weight: 2 }).addTo(g);
      const v = narrationVerdict(n, r.closest.get(n.pageId));
      L.circleMarker([n.landmark.lat, n.landmark.lon], { className: `lf-lm ${v.tone}`, radius: 7, weight: 2 })
        .bindTooltip(`${i + 1}. ${n.landmark.title}`, { className: 'lm', direction: 'top', offset: [0, -6] })
        .on('click', () => this.onSelect(i))
        .addTo(g);
      g.addTo(this.sections);
      this.sectionLayers.push(g);
    });
    // Landmarks that were near but never spoke, so the reader can see what was skipped.
    for (const d of r.dropped) {
      L.circleMarker([d.landmark.lat, d.landmark.lon], { className: 'lf-lm dropped', radius: 5, weight: 1 })
        .bindTooltip(`${d.landmark.title} (skipped)`, { className: 'lm', direction: 'top', offset: [0, -6] })
        .addTo(this.sections);
    }
  }

  /** Path samples between two drive times, as Leaflet points. */
  pathBetween(r: SimResult, fromSec: number, toSec: number): [number, number][] {
    return r.path.filter((p) => p.t >= fromSec && p.t <= toSec).map((p) => [p.lat, p.lon]);
  }

  fitSection(r: SimResult, n: SimNarration): void {
    const pts = this.pathBetween(r, Math.min(n.triggeredAt, n.startPos?.t ?? n.triggeredAt), n.endedAt ?? r.durationSec);
    pts.push([n.landmark.lat, n.landmark.lon]);
    this.map.fitBounds(L.latLngBounds(pts), { padding: [60, 60], maxZoom: 14 });
  }

  highlight(i: number | null): void {
    this.sectionLayers.forEach((g, k) => {
      g.eachLayer((l) => {
        const el = (l as L.Path).getElement?.();
        if (el) el.classList.toggle('dim', i != null && k !== i);
      });
    });
  }

  moveCar(lat: number, lon: number): void {
    if (!this.car) {
      this.car = L.marker([lat, lon], { icon: L.divIcon({ className: 'car', iconSize: [18, 18] }), zIndexOffset: 1000, interactive: false }).addTo(this.map);
    } else {
      this.car.setLatLng([lat, lon]);
    }
  }

  hideCar(): void {
    this.car?.remove();
    this.car = null;
  }

  /** Two clicks: start, then end. The callback fires once with both. */
  pick(onDone: (a: LatLon, b: LatLon) => void, onFirst: (a: LatLon) => void): () => void {
    this.picks.clearLayers();
    let first: LatLon | null = null;
    this.map.getContainer().classList.add('picking');
    const stop = (): void => {
      this.pickHandler = null;
      this.map.getContainer().classList.remove('picking');
    };
    this.pickHandler = (p) => {
      L.circleMarker([p.lat, p.lon], { className: `lf-pick ${first ? 'end' : 'start'}`, radius: 7, weight: 2 }).addTo(this.picks);
      if (!first) {
        first = p;
        onFirst(p);
        return;
      }
      const a = first;
      stop();
      onDone(a, p);
    };
    return stop;
  }
}
